import * as React from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';

import { cn } from '@/ux-common/lib/utils';

/**
 * AlertDialog — a Dialog that demands a choice.
 *
 * Visually identical to `Dialog`: it reuses the same `.dialog*` classes and
 * ships no CSS of its own. What differs is semantics — `role="alertdialog"`,
 * and Radix's `AlertDialog` primitive blocks outside-click dismissal, so a
 * stray click cannot abandon the decision.
 *
 * **Escape still closes** — it dismisses the dialog via `onOpenChange(false)`.
 * Radix's alert-dialog ships no `onEscapeKeyDown` handler, so the key falls
 * through to the underlying dismissable layer. The outcome is *equivalent to
 * cancelling* — nothing is confirmed — but Escape does **not** invoke
 * `AlertDialogCancel`'s handler, so cancel-path side effects belong in
 * `onOpenChange`, not only on the Cancel button's `onClick`.
 *
 * Escape closing at all is deliberate: the WAI-ARIA dialog pattern
 * `alertdialog` inherits makes Escape the universal keyboard exit, and
 * suppressing it strands keyboard users. The forced-choice guarantee here is
 * about *visual* affordances — exactly two, Cancel and Action — not about
 * disabling a keyboard convention.
 *
 * **There is no close-X export, and the header must not have one.** A
 * forced-choice confirmation offers exactly two exits — Cancel and Action. An
 * X is a third and the least deliberate of them, so the header is title-only:
 * do not put `HeaderActions` inside an `AlertDialogContent`. Design confirmed
 * this on UXD-31; the sticker sheet's Destructive cell draws an X and that
 * cell is wrong, not the component.
 *
 * **Wanting a close X means the wrong component.** That instinct says
 * dismissing without choosing is acceptable — which is what `Dialog` is for,
 * and `Dialog` ships `DialogCloseButton` for exactly this. Use `Dialog` for
 * anything dismissible; use `AlertDialog` only when abandoning the decision is
 * itself a decision.
 *
 * **No motion.** Same as `Dialog`: the Figma specifies no enter or exit
 * animation, so none is shipped — deliberately unlike the legacy CKDS
 * dialog's zoom-from-top-left.
 *
 * @example
 * ```tsx
 * <AlertDialog>
 *   <AlertDialogTrigger asChild><Button className="btn-destructive">Delete</Button></AlertDialogTrigger>
 *   <AlertDialogContent className="dialog-sm">
 *     <Header>
 *       <HeaderIcon><Icon className="icon-critical icon-lg"><TriangleAlert className="glyph glyph-lg" aria-hidden /></Icon></HeaderIcon>
 *       <HeaderTitle>
 *         <AlertDialogTitle asChild>
 *           <Typography as="h2" className="text-header-section">Destructive?</Typography>
 *         </AlertDialogTitle>
 *       </HeaderTitle>
 *     </Header>
 *     <AlertDialogBody>
 *       <AlertDialogDescription asChild>
 *         <Typography className="text-body-base">This cannot be undone.</Typography>
 *       </AlertDialogDescription>
 *     </AlertDialogBody>
 *     <AlertDialogFooter>
 *       <AlertDialogFooterButtons>
 *         <AlertDialogCancel asChild><Button className="btn-tier2">No, don't delete</Button></AlertDialogCancel>
 *         <AlertDialogAction asChild><Button className="btn-destructive">Yes, delete</Button></AlertDialogAction>
 *       </AlertDialogFooterButtons>
 *     </AlertDialogFooter>
 *   </AlertDialogContent>
 * </AlertDialog>
 * ```
 */
export const AlertDialog = AlertDialogPrimitive.Root;

export const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

export const AlertDialogPortal = AlertDialogPrimitive.Portal;

export const AlertDialogTitle = AlertDialogPrimitive.Title;

export const AlertDialogDescription = AlertDialogPrimitive.Description;

export const AlertDialogAction = AlertDialogPrimitive.Action;

/**
 * AlertDialogCancel — the safe exit, and the element Radix moves initial focus
 * to on open (see AGENTS.md "Focus behavior").
 *
 * Wrapped rather than re-exported bare so it carries `data-component`, which
 * the theme needs as a styling hook: that initial focus is programmatic, and
 * `:focus-visible` does not match a programmatic focus when the dialog was
 * opened by pointer, so the ring has to be attached to plain `:focus` for this
 * one element. `data-component` is set before the spread, so a caller can
 * still override it — and it survives `asChild`, because Slot merges it onto
 * the child, whose own `data-component` is likewise set before its spread.
 */
export type AlertDialogCancelProps = React.ComponentPropsWithoutRef<
  typeof AlertDialogPrimitive.Cancel
>;

export const AlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  AlertDialogCancelProps
>((props, ref) => (
  <AlertDialogPrimitive.Cancel ref={ref} data-component="AlertDialogCancel" {...props} />
));
AlertDialogCancel.displayName = 'AlertDialogCancel';

/**
 * AlertDialogOverlay — the scrim. Shares `.dialog-overlay` with Dialog.
 */
export type AlertDialogOverlayProps = React.ComponentPropsWithoutRef<
  typeof AlertDialogPrimitive.Overlay
>;

export const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  AlertDialogOverlayProps
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    ref={ref}
    data-component="AlertDialogOverlay"
    className={cn('dialog-overlay', className)}
    {...props}
  />
));
AlertDialogOverlay.displayName = 'AlertDialogOverlay';

/**
 * AlertDialogContent — the surface. Shares `.dialog` and its size modifiers
 * with Dialog. An outside click does not dismiss it; Escape does, via
 * `onOpenChange(false)` rather than by activating `AlertDialogCancel`.
 */
export type AlertDialogContentProps = React.ComponentPropsWithoutRef<
  typeof AlertDialogPrimitive.Content
> & {
  /** Class applied to the scrim rather than the surface. */
  overlayClassName?: string;
};

export const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  AlertDialogContentProps
>(({ overlayClassName, className, children, ...props }, ref) => (
  <AlertDialogPortal>
    <AlertDialogOverlay className={overlayClassName} />
    <AlertDialogPrimitive.Content
      ref={ref}
      data-component="AlertDialogContent"
      className={cn('dialog', className)}
      {...props}
    >
      {children}
    </AlertDialogPrimitive.Content>
  </AlertDialogPortal>
));
AlertDialogContent.displayName = 'AlertDialogContent';

/**
 * AlertDialogBody — the scrolling content region. Shares `.dialog-body`.
 */
export type AlertDialogBodyProps = React.HTMLAttributes<HTMLDivElement>;

export const AlertDialogBody = React.forwardRef<HTMLDivElement, AlertDialogBodyProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-component="AlertDialogBody"
      className={cn('dialog-body', className)}
      {...props}
    />
  )
);
AlertDialogBody.displayName = 'AlertDialogBody';

/**
 * AlertDialogFooter — layout-only action row. Shares `.dialog-footer`.
 */
export type AlertDialogFooterProps = React.HTMLAttributes<HTMLDivElement>;

export const AlertDialogFooter = React.forwardRef<HTMLDivElement, AlertDialogFooterProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-component="AlertDialogFooter"
      className={cn('dialog-footer', className)}
      {...props}
    />
  )
);
AlertDialogFooter.displayName = 'AlertDialogFooter';

/**
 * AlertDialogFooterButtons — equal-width, full-span button row. Shares
 * `.dialog-footer-buttons`.
 */
export type AlertDialogFooterButtonsProps = React.HTMLAttributes<HTMLDivElement>;

export const AlertDialogFooterButtons = React.forwardRef<
  HTMLDivElement,
  AlertDialogFooterButtonsProps
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-component="AlertDialogFooterButtons"
    className={cn('dialog-footer-buttons', className)}
    {...props}
  />
));
AlertDialogFooterButtons.displayName = 'AlertDialogFooterButtons';
